// JJOS .PAC parser.
//
// A PAC file is a RIFF/WAVE container with a normal PCM data chunk and a
// large smpl sampler-data area. The sampler-data block contains:
//   - a 16-byte PAC header
//   - 64 packed sample records, 64 bytes each
//   - a packed program block whose pad records mirror MPC1000 .PGM records
//
// The browser audio engine already knows how to decode WAV ArrayBuffers, so
// this parser reconstructs one small standard WAV per packed sample and
// returns the embedded program in the same shape parsePGM() returns.
import { attachProgramNoteResolver } from './program-note-resolution.js';

const PAD_RECORD_SIZE = 0xa4;
const SAMPLE_SLOT_SIZE = 0x18;
const PAD_COUNT = 64;
const SAMPLES_PER_PAD = 4;
// Exported so tests can build a byte-accurate synthetic .PAC without
// hand-copying these offsets (and silently drifting if they ever change).
export const PAC_HEADER_SIZE = 0x10;
export const PAC_SAMPLE_RECORD_SIZE = 0x40;
export const PAC_SAMPLE_RECORD_COUNT = 64;
const PAC_SAMPLE_NAME_OFFSET = 0x2c;
const PAC_SAMPLE_NAME_LENGTH = 20;
const PAC_PROGRAM_SIZE_OFFSET = 0x04;
const PAC_PROGRAM_NAME_OFFSET = 0x08;
export const PAC_PROGRAM_PAD_OFFSET = 0x1c;
const PAC_PROGRAM_PAD_TO_NOTE_OFFSET = 0x291c;
export const PAC_PROGRAM_NOTE_TO_PAD_OFFSET = 0x295c;
export const NOTE_TO_PAD_SIZE = 128;

const PAC_PROFILE = Object.freeze({
  id: 'jjos-pac',
  kind: 'pac',
  os: 'jjos',
  label: 'JJOS .PAC packed program',
  version: 'MPC1000 PAC',
  supported: true,
  knownVersion: 'JJOS .PAC',
  quirks: {},
});

function readAsciiZ(view, offset, length) {
  const bytes = new Uint8Array(view.buffer, view.byteOffset + offset, length);
  let end = bytes.indexOf(0);
  if (end === -1) end = length;
  return new TextDecoder('ascii').decode(bytes.subarray(0, end)).trim();
}

function readFourCC(bytes, offset) {
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

function writeFourCC(bytes, offset, id) {
  for (let i = 0; i < 4; i++) bytes[offset + i] = id.charCodeAt(i);
}

function findRiffChunks(view, bytes) {
  if (view.byteLength < 12 || readFourCC(bytes, 0) !== 'RIFF' || readFourCC(bytes, 8) !== 'WAVE') {
    throw new Error('Unsupported or corrupt .PAC file: expected a RIFF/WAVE container.');
  }

  const chunks = new Map();
  let offset = 12;
  while (offset + 8 <= view.byteLength) {
    const id = readFourCC(bytes, offset);
    const size = view.getUint32(offset + 4, true);
    const bodyOffset = offset + 8;
    if (bodyOffset + size > view.byteLength) break;
    if (!chunks.has(id)) chunks.set(id, { id, offset, bodyOffset, size });
    offset = bodyOffset + size + (size % 2);
  }
  return chunks;
}

function parseFmt(view, fmtChunk) {
  if (!fmtChunk || fmtChunk.size < 16) {
    throw new Error('Unsupported or corrupt .PAC file: required fmt chunk is missing.');
  }
  const offset = fmtChunk.bodyOffset;
  const audioFormat = view.getUint16(offset, true);
  const channels = view.getUint16(offset + 2, true);
  const sampleRate = view.getUint32(offset + 4, true);
  const bitsPerSample = view.getUint16(offset + 14, true);
  const bytesPerFrame = channels * (bitsPerSample / 8);
  if (audioFormat !== 1 || !channels || !sampleRate || !bytesPerFrame || !Number.isInteger(bytesPerFrame)) {
    throw new Error('Unsupported .PAC audio format: only PCM WAVE data is supported.');
  }
  return { audioFormat, channels, sampleRate, bitsPerSample, bytesPerFrame };
}

function buildWavBuffer(fmtBytes, pcmBytes) {
  const fmtPad = fmtBytes.length % 2;
  const dataPad = pcmBytes.length % 2;
  const totalBytes = 12 + 8 + fmtBytes.length + fmtPad + 8 + pcmBytes.length + dataPad;
  const buffer = new ArrayBuffer(totalBytes);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  writeFourCC(bytes, 0, 'RIFF');
  view.setUint32(4, totalBytes - 8, true);
  writeFourCC(bytes, 8, 'WAVE');

  let offset = 12;
  writeFourCC(bytes, offset, 'fmt ');
  view.setUint32(offset + 4, fmtBytes.length, true);
  offset += 8;
  bytes.set(fmtBytes, offset);
  offset += fmtBytes.length + fmtPad;

  writeFourCC(bytes, offset, 'data');
  view.setUint32(offset + 4, pcmBytes.length, true);
  offset += 8;
  bytes.set(pcmBytes, offset);

  return buffer;
}

function parsePacSamples({ view, bytes, smplChunk, fmtChunk, dataChunk }) {
  if (!smplChunk || smplChunk.size < 36) {
    throw new Error('Unsupported or corrupt .PAC file: required smpl chunk is missing.');
  }
  if (!dataChunk) {
    throw new Error('Unsupported or corrupt .PAC file: required data chunk is missing.');
  }

  const fmt = parseFmt(view, fmtChunk);
  const smplBody = smplChunk.bodyOffset;
  const numLoops = view.getUint32(smplBody + 28, true);
  const samplerDataSize = view.getUint32(smplBody + 32, true);
  const samplerDataOffset = smplBody + 36 + numLoops * 24;
  const sampleTableOffset = samplerDataOffset + PAC_HEADER_SIZE;
  const programBase = sampleTableOffset + PAC_SAMPLE_RECORD_COUNT * PAC_SAMPLE_RECORD_SIZE;
  const smplEnd = smplChunk.bodyOffset + smplChunk.size;
  const samplerDataEnd = samplerDataOffset + samplerDataSize;

  if (
    !samplerDataSize ||
    samplerDataEnd > smplEnd ||
    programBase + PAC_PROGRAM_NOTE_TO_PAD_OFFSET + NOTE_TO_PAD_SIZE > samplerDataEnd
  ) {
    throw new Error('Unsupported or corrupt .PAC file: packed sampler-data block is incomplete.');
  }

  const fmtBytes = bytes.subarray(fmtChunk.bodyOffset, fmtChunk.bodyOffset + fmtChunk.size);
  const totalFrames = Math.floor(dataChunk.size / fmt.bytesPerFrame);
  const samples = [];

  for (let i = 0; i < PAC_SAMPLE_RECORD_COUNT; i++) {
    const recordOffset = sampleTableOffset + i * PAC_SAMPLE_RECORD_SIZE;
    const name = readAsciiZ(view, recordOffset + PAC_SAMPLE_NAME_OFFSET, PAC_SAMPLE_NAME_LENGTH);
    const frameCount = view.getUint32(recordOffset + 0x04, true);
    const startFrame = view.getUint32(recordOffset + 0x08, true);
    if (!name || !frameCount || startFrame >= totalFrames) continue;

    const endFrame = Math.min(startFrame + frameCount, totalFrames);
    if (endFrame <= startFrame) continue;
    const byteStart = dataChunk.bodyOffset + startFrame * fmt.bytesPerFrame;
    const byteEnd = dataChunk.bodyOffset + endFrame * fmt.bytesPerFrame;
    const pcmBytes = bytes.subarray(byteStart, byteEnd);
    const truncatedName = name.slice(0, 16).trim();
    samples.push({
      index: i,
      name,
      aliases: truncatedName && truncatedName !== name ? [truncatedName] : [],
      startFrame,
      frameCount: endFrame - startFrame,
      rootNote: view.getUint32(recordOffset + 0x24, true),
      wavBuffer: buildWavBuffer(fmtBytes, pcmBytes),
    });
  }

  return { samples, programBase };
}

function parseSamplesForPad(view, padOff) {
  const samples = [];
  for (let s = 0; s < SAMPLES_PER_PAD; s++) {
    const sOff = padOff + s * SAMPLE_SLOT_SIZE;
    const sampleName = readAsciiZ(view, sOff, 16);
    if (!sampleName) continue;
    samples.push({
      name: sampleName,
      level: view.getUint8(sOff + 0x11),
      rangeLower: view.getUint8(sOff + 0x12),
      rangeUpper: view.getUint8(sOff + 0x13),
      tuning: view.getInt16(sOff + 0x14, true),
      playMode: view.getUint8(sOff + 0x16),
      chopIndex1Based: view.getUint8(sOff + 0x17),
    });
  }
  return samples;
}

function applyChopFamilyDetection(pads) {
  const padsByName = new Map();
  for (const pad of pads) {
    const name = pad.samples[0]?.name;
    if (!name) continue;
    if (!padsByName.has(name)) padsByName.set(name, []);
    padsByName.get(name).push(pad);
  }
  for (const pad of pads) {
    pad.chopTotalSlices = 1;
    const name = pad.samples[0]?.name;
    const group = name ? padsByName.get(name) : null;
    if (!group || group.length < 2) continue;

    const padLevelIndices = group.map((p) => p.chopIndex).sort((a, b) => a - b);
    const isPadLevelRun = padLevelIndices.every((v, i) => v === i);
    if (isPadLevelRun) {
      pad.chopTotalSlices = padLevelIndices.length;
      if (pad.storedChopIndex1Based > 0) pad.storedChopIndex = pad.storedChopIndex1Based - 1;
      continue;
    }

    const sampleSlotIndices = group
      .map((p) => p.samples[0]?.chopIndex1Based ?? 0)
      .sort((a, b) => a - b);
    const isSampleSlotRun = sampleSlotIndices.every((v, i) => v === i + 1);
    if (isSampleSlotRun) {
      pad.chopIndex = (pad.samples[0]?.chopIndex1Based ?? 1) - 1;
      pad.chopTotalSlices = sampleSlotIndices.length;
      if (pad.storedChopIndex1Based > 0) pad.storedChopIndex = pad.storedChopIndex1Based - 1;
    }
  }
}

function parsePacProgram(view, bytes, programBase, fallbackName) {
  const fileSize = view.getUint32(programBase + PAC_PROGRAM_SIZE_OFFSET, true);
  const name = readAsciiZ(view, programBase + PAC_PROGRAM_NAME_OFFSET, 16) || fallbackName;
  const pads = [];

  for (let p = 0; p < PAD_COUNT; p++) {
    const padOff = programBase + PAC_PROGRAM_PAD_OFFSET + p * PAD_RECORD_SIZE;
    pads.push({
      index: p,
      samples: parseSamplesForPad(view, padOff),
      voiceOverlap: view.getUint8(padOff + 0x62),
      muteGroup: view.getUint8(padOff + 0x63),
      attack: view.getUint8(padOff + 0x66),
      decay: view.getUint8(padOff + 0x67),
      decayMode: view.getUint8(padOff + 0x68),
      sustain: view.getUint8(padOff + 0x69),
      release: view.getUint8(padOff + 0x6a),
      velocityToLevel: view.getUint8(padOff + 0x6b),
      filter1Type: view.getInt8(padOff + 0x71),
      filter1Freq: view.getUint8(padOff + 0x72),
      filter1Res: view.getUint8(padOff + 0x73),
      filter1VelToFreq: view.getUint8(padOff + 0x78),
      filter2Type: view.getInt8(padOff + 0x79),
      filter2Freq: view.getUint8(padOff + 0x7a),
      filter2Res: view.getUint8(padOff + 0x7b),
      filter2VelToFreq: view.getUint8(padOff + 0x80),
      mixerLevel: view.getUint8(padOff + 0x8f),
      mixerPan: view.getUint8(padOff + 0x90),
      output: view.getInt8(padOff + 0x91),
      fxSend: view.getInt8(padOff + 0x92),
      fxSendLevel: view.getUint8(padOff + 0x93),
      filterAttenuation: view.getUint8(padOff + 0x94),
      storedChopIndex1Based: view.getUint8(padOff + 0x96),
      chopIndex: view.getUint8(padOff + 0x5c),
    });
  }
  applyChopFamilyDetection(pads);

  const padToNote = new Uint8Array(bytes.buffer, bytes.byteOffset + programBase + PAC_PROGRAM_PAD_TO_NOTE_OFFSET, PAD_COUNT);
  const noteToPad = new Uint8Array(bytes.buffer, bytes.byteOffset + programBase + PAC_PROGRAM_NOTE_TO_PAD_OFFSET, NOTE_TO_PAD_SIZE);

  return attachProgramNoteResolver({
    kind: 'pac-program',
    name,
    fileSize,
    version: PAC_PROFILE.version,
    profile: PAC_PROFILE,
    sourceFileType: 'PAC',
    pads,
    padToNote: Array.from(padToNote),
    noteToPad: Array.from(noteToPad),
  });
}

export function parsePAC(arrayBuffer, name = '') {
  const view = new DataView(arrayBuffer);
  const bytes = new Uint8Array(arrayBuffer);
  const chunks = findRiffChunks(view, bytes);
  const { samples, programBase } = parsePacSamples({
    view,
    bytes,
    smplChunk: chunks.get('smpl'),
    fmtChunk: chunks.get('fmt '),
    dataChunk: chunks.get('data'),
  });
  const program = parsePacProgram(view, bytes, programBase, name);
  return {
    kind: 'pac',
    name: program.name || name,
    version: PAC_PROFILE.version,
    profile: PAC_PROFILE,
    program,
    samples,
  };
}
